// Image diagram element (spec §7, P5). The Connect-native target a Desktop `.pipe`
// `image` annotation maps into. `position` (top-left) + `size` are Connect canvas
// coords (Y-down), transformed on import like shape/text.
//
// Two forms, one schema — because the client-side import transformer PARSES the
// system (desktop-json-transformer HydraulicSystemSchema.parse) BEFORE the server-side
// extraction runs:
//   - Import (transitional): `image_data` carries inline base64 the converter emits.
//   - Persisted: `imageRef` references the tenant-scoped asset store (spec §4.1-4.2);
//     server extraction (createModel/updateModel) strips image_data → imageRef so bytes
//     never reach the jsonb.
// Kept a plain ZodObject (no `.refine`) so it stays usable inside the
// `diagram_elements` discriminatedUnion. Exactly one of image_data / imageRef is
// expected; extraction enforces the persisted invariant.

import { z } from 'zod';
import { PointSchema } from '../base/common';

/** Raster types the converter emits / Connect renders. */
export const ImageMimeSchema = z.enum(['image/png', 'image/jpeg']);
export type ImageMimeType = z.infer<typeof ImageMimeSchema>;

/** Reference to a stored image asset (bytes live in object storage, keyed by hash). */
export const ImageRefSchema = z.object({
  /** SHA-256 hex of the raw image bytes; the object key + dedup key within a tenant. */
  contentHash: z.string().min(1),
  mime: ImageMimeSchema,
});
export type ImageRef = z.infer<typeof ImageRefSchema>;

export const ImageElementSchema = z.object({
  id: z.string(),
  kind: z.literal('image'),
  position: PointSchema,
  size: z.object({
    width: z.number().nonnegative(),
    height: z.number().nonnegative(),
  }),
  /** Persisted form: reference into the tenant-scoped asset store. */
  imageRef: ImageRefSchema.optional(),
  /** Import-only transitional form: inline base64 bytes, stripped by server extraction. */
  image_data: z.string().min(1).optional(),
  /** Mime of the inline bytes (import form); the persisted mime lives in imageRef. */
  mime: ImageMimeSchema.optional(),
  locked: z.boolean().default(false),
});
export type ImageElement = z.infer<typeof ImageElementSchema>;
